import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  Output,
  ViewChild
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { VatAmountCalculator } from '@zaoblako/shared/common/classes/vat.amount.calculator';
import type { ApprovalSchemeFindInterface } from '@zaoblako/shared/modules/catalog/dtos/approval-scheme/approval-scheme-find.dto';
import type { BankFindInterface } from '@zaoblako/shared/modules/catalog/dtos/bank/bank-find.dto';
import { CurrencyFindInterface } from '@zaoblako/shared/modules/catalog/dtos/currency/currency-find.dto';
import type { DirectionFindInterface } from '@zaoblako/shared/modules/catalog/dtos/direction/direction-find.dto';
import type { FilialFindInterface } from '@zaoblako/shared/modules/catalog/dtos/filial/filial-find.dto';
import type { UnitFindInterface } from '@zaoblako/shared/modules/catalog/dtos/unit/unit-find.dto';
import type { CompanyFindInterface } from '@zaoblako/shared/modules/client/dtos/company/find/company-find.dto';
import type { NdsFindInterface } from '@zaoblako/shared/modules/finance/dtos/nds-find.dto';
import type { UserGroupFindInterface } from '@zaoblako/shared/modules/security/dtos/user-group/user-group-find.dto';
import type { UserFindInterface } from '@zaoblako/shared/modules/security/dtos/user/user-find.dto';
import type { FileFindInterface } from '@zaoblako/shared/modules/storage/dtos/file-find.dto';
import type { TaskStatusFindInterface } from '@zaoblako/shared/modules/task/dtos/status/task-status-find.dto';
import type { TaskStatusFormSettingsItemInterface } from '@zaoblako/shared/modules/task/dtos/status/task-status-form-settings-item.dto';
import type { TaskStatusFormSettingsInterface } from '@zaoblako/shared/modules/task/dtos/status/task-status-form-settings.dto';
import {
  TaskFormDto,
  type TaskFormInterface,
  type TaskFormOperation
} from '@zaoblako/shared/modules/task/dtos/task-form.dto';
import type { TaskPriorityFindInterface } from '@zaoblako/shared/modules/task/dtos/task-priority-find.dto';
import { TaskKindEnum } from '@zaoblako/shared/modules/task/enum/task-kind.enum';
import { TaskPaymentTypeEnum } from '@zaoblako/shared/modules/task/enum/task.payment.type.enum';
import { ConfirmDialogService } from '@zaoblako/web/core/services/confirm-dialog.service';
import { ErrorService } from '@zaoblako/web/core/services/error.service';
import { FileService } from '@zaoblako/web/core/services/file.service';
import { TaskEditPermissions } from '@zaoblako/web/core/types/task-edit-permissions.type';
import { CardContragentInfoComponent } from '@zaoblako/web/modules/payments/dumbs/card-contragent-info/card-contragent-info.component';
import { PaymentOrderTaskComponent } from '@zaoblako/web/modules/payments/dumbs/payment-order-task/payment-order-task.component';
import { TaskEditFormComponent } from '@zaoblako/web/modules/payments/dumbs/task-edit-form/task-edit-form.component';
import { GoBackButtonComponent } from '@zaoblako/web/shared/components/go-back-button/go-back-button.component';
import { DealAutoCompleteComponent } from '@zaoblako/web/shared/dumbs/autoComplete/deal-auto-complete/deal-auto-complete.component';
import { BaseFormComponent } from '@zaoblako/web/shared/dumbs/base-form/base-form.component';
import { DatepickerComponent } from '@zaoblako/web/shared/dumbs/datepicker/datepicker.component';
import { ApprovalSchemeSingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/approval-scheme-single-select/approval-scheme-single-select.component';
import { BudgetClassificationCodeSingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/budget-classification-code-single-select/budget-classification-code-single-select.component';
import { CostCenterSingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/cost-center-single-select/cost-center-single-select.component';
import { CurrencySingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/currency-single-select/currency-single-select.component';
import { DirectionSingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/direction-single-select/direction-single-select.component';
import { FilialSingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/filial-single-select/filial-single-select.component';
import { MultiSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/multi-select/multi-select.component';
import { PayerStatusSingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/payer-status-single-select/payer-status-single-select.component';
import { SingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/single-select/single-select.component';
import { UnitSingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/unit-single-select/unit-single-select.component';
import { UserMultiSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/user-multi-select/user-multi-select.component';
import { UserSingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/user-single-select/user-single-select.component';
import { EditorComponent } from '@zaoblako/web/shared/dumbs/editor/editor.component';
import { FileComponent } from '@zaoblako/web/shared/dumbs/file/file.component';
import { InputTextComponent } from '@zaoblako/web/shared/dumbs/input-text/input-text.component';
import { UserCardPreviewComponent } from '@zaoblako/web/shared/dumbs/user-card-preview/user-card-preview.component';
import { JoinPipe } from '@zaoblako/web/shared/pipes/join.pipe';
import { PaymentTypePipe } from '@zaoblako/web/shared/pipes/payment-type.pipe';
import { TaskKindNamePipe } from '@zaoblako/web/shared/pipes/task-kind-name.pipe';
import * as moment from 'moment-timezone';
import { MenuItem } from 'primeng/api';
import { CardModule } from 'primeng/card';
import { CheckboxModule } from 'primeng/checkbox';
import { DividerModule } from 'primeng/divider';
import { FileUpload, FileUploadModule } from 'primeng/fileupload';
import { InputNumberModule } from 'primeng/inputnumber';
import { StepsModule } from 'primeng/steps';
import '@zaoblako/shared/modules/client/dtos/company.group/company-group-find.dto';
import '@zaoblako/shared/modules/project/dto/checklist-type/checklist-type-find.dto';

@Component({
  standalone: true,
  selector: 'crm-task-form',
  templateUrl: './task-form.component.html',
  imports: [
    CommonModule,
    TaskEditFormComponent,
    StepsModule,
    CardModule,
    SingleSelectComponent,
    JoinPipe,
    GoBackButtonComponent,
    CardContragentInfoComponent,
    DirectionSingleSelectComponent,
    InputTextComponent,
    PaymentTypePipe,
    DatepickerComponent,
    InputNumberModule,
    FormsModule,
    FileComponent,
    FileUploadModule,
    EditorComponent,
    MultiSelectComponent,
    PaymentOrderTaskComponent,
    DealAutoCompleteComponent,
    UserCardPreviewComponent,
    DividerModule,
    UnitSingleSelectComponent,
    FilialSingleSelectComponent,
    ApprovalSchemeSingleSelectComponent,
    UserSingleSelectComponent,
    UserMultiSelectComponent,
    CostCenterSingleSelectComponent,
    TaskKindNamePipe,
    BudgetClassificationCodeSingleSelectComponent,
    PayerStatusSingleSelectComponent,
    CheckboxModule,
    CurrencySingleSelectComponent
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TaskFormComponent extends BaseFormComponent<TaskFormInterface> {
  public engine: 'class-transformer' | 'serde' = 'serde';

  @Input() public task = <TaskFormInterface>{};
  @Input() public operation: TaskFormOperation = 'create';
  public dto = TaskFormDto;

  @Input() public organizations: CompanyFindInterface[] = [];
  @Input() public filials: FilialFindInterface[] = [];
  @Input() public units: UnitFindInterface[] = [];
  @Input() public directions: DirectionFindInterface[] = [];
  @Input() public priorities: TaskPriorityFindInterface[] = [];
  @Input() public nds: NdsFindInterface[] = [];
  @Input() public banks: BankFindInterface[] = [];
  @Input() public users: UserFindInterface[] = [];
  @Input() public userGroups: UserGroupFindInterface[] = [];
  @Input() public statuses: TaskStatusFindInterface[] = [];
  @Input() public schemes: ApprovalSchemeFindInterface[] = [];
  @Input() public currencies: CurrencyFindInterface[] = [];

  @Output() public bankSearch = new EventEmitter<string>();
  @Output() public changeOrganization = new EventEmitter<void>();

  @Input() public permissions: TaskEditPermissions = {};

  @ViewChild('fileUpload') public fileUpload: FileUpload;
  public uploadingFiles = false;

  public types: TaskPaymentTypeEnum[] = Object.values(TaskPaymentTypeEnum);
  public kinds: TaskKindEnum[] = Object.values(TaskKindEnum);

  public get TaskKindEnum() {
    return TaskKindEnum;
  }

  @Input() public activeStep = 0;
  public steps: MenuItem[] = [
    {
      label: 'Выбор статуса и типа'
    },
    {
      label: 'Заполнение полей'
    }
  ];

  public constructor(
    private readonly confirmDialogService: ConfirmDialogService,
    private readonly fileService: FileService,
    private readonly cdRef: ChangeDetectorRef,
    private readonly errorService: ErrorService
  ) {
    super();
  }

  public selectedStatus() {
    this.activeStep = 1;
    this.cdRef.markForCheck();
  }

  public onSave() {
    const saveMethod = () => {
      if (!this.task.orders?.length) {
        this.task.orders = [
          {
            subject: this.task.subject,
            date: this.task.date,
            amount: this.task.amount
          }
        ];
      }

      if (
        this.checkValidate(this.task).valid &&
        !this.checkRequiredFields() &&
        !(this.task.subject?.length > 190 || this.task.orders?.filter((order) => order.subject?.length > 190)?.length)
      ) {
        super.onSave(this.task);
      }
    };

    if (this.task.responsible?.approvalScheme && !this.task.scheme) {
      this.task.scheme = this.task.responsible.approvalScheme;
    }

    if (this.task.orders?.length > 1 && !this.isEqualSumTask()) {
      return this.errorService.addCustomError('Ошибка', 'Не совпадают суммы заявки и платежных поручений');
    }

    if (this.task.orders?.length === 1 && !this.isEqualPaymentTask()) {
      this.confirmDialogService.confirm({
        message: 'Не совпадают данные заявки и платежного поручения, обновить данные в платежном поручении?',
        accept: () => {
          this.task.orders?.forEach((order) => {
            order.subject = this.task.subject;
            order.date = this.task.date;
            order.amount = this.task.amount;
          });
          this.cdRef.markForCheck();
          saveMethod();
        },
        reject: () => {
          saveMethod();
        }
      });
    } else {
      saveMethod();
    }
  }

  private checkRequiredFields() {
    this.errors = Object.create({});
    Object.entries(this.task.status.formSettings).forEach(
      ([property, setting]: [keyof TaskStatusFormSettingsInterface, TaskStatusFormSettingsItemInterface]) => {
        if (
          setting.required &&
          ((property !== 'vat' && !this.task[property]) ||
            (property === 'vat' && (typeof this.task[property] === 'undefined' || this.task[property] === null)))
        ) {
          this.errors = {
            ...this.errors,
            [property]: `Поле "${setting.name}" обязательно для заполнения`
          };
        }
      }
    );
    return Object.keys(this.errors)?.length;
  }

  public uploadFiles(data: { files: FileList }) {
    this.uploadingFiles = true;
    this.fileService.upload(data.files).subscribe({
      next: (files) => {
        this.task.files = files;
        this.fileUpload.clear();
        this.uploadingFiles = false;
      },
      error: () => (this.uploadingFiles = false)
    });
  }

  public deleteFile(filesArray: FileFindInterface[], file: FileFindInterface) {
    this.confirmDialogService.confirm({
      message: `Вы действительно хотите удалить файл "${file.name}"?`,
      accept: () => {
        filesArray.splice(filesArray.indexOf(file), 1);
        this.cdRef.markForCheck();
      }
    });
  }

  public changeValue() {
    if (this.task.nds?.amount !== null && this.task.nds?.amount !== undefined) {
      this.task.vat = VatAmountCalculator.getInstance().getVatAmount(this.task.amount || 0, this.task.nds?.amount || 0);
    }
  }

  public backStep() {
    this.activeStep = 0;
    this.task.status = null;
    this.errors = {};
    this.cdRef.markForCheck();
  }

  private isEqualPaymentTask(): boolean {
    return (
      this.task.orders?.length &&
      this.task.orders[0].amount === this.task.amount &&
      moment(this.task.orders[0].date).isSame(moment(this.task.date), 'day') &&
      this.task.orders[0].subject.trim().toLowerCase() === this.task.subject.trim().toLowerCase()
    );
  }

  private isEqualSumTask(): boolean {
    return this.task.orders?.reduce((sum, order) => sum + (order.amount || 0), 0) === (this.task.amount || 0);
  }

  public changeUsersAndGroups(scheme: ApprovalSchemeFindInterface) {
    if (!this.canDelete) {
      this.task.userAuditors = [];
      this.task.userGroupAuditors = [];
      if (scheme.userAuditors?.length) {
        this.task.userAuditors = scheme.userAuditors;
      }
      if (scheme?.userGroupAuditors?.length) {
        this.task.userGroupAuditors = scheme.userGroupAuditors;
      }
    }
  }

  public changeKind() {
    if (!this.task.kind) {
      this.task.kind = null;
    }
  }

  public changeResponsible(user: UserFindInterface) {
    if (user.approvalScheme && !this.task.scheme) {
      this.task.scheme = user.approvalScheme;
    }
  }
}
